import axios from "axios";
import { Controller } from "stimulus";

interface TagResult {
  id: string;
  name: string;
  usage_count: number;
  heading: string;
}

function csrfToken(): string {
  return (
    document
      .querySelector("meta[name='csrf-token']")
      ?.getAttribute("content") || ""
  );
}

export default class extends Controller {
  static targets = ["panel", "input", "results"];
  static values = {
    searchUrl: String,
    submitUrl: String,
    communityMemberIds: Array,
    headings: Array,
  };

  declare panelTarget: HTMLElement;
  declare inputTarget: HTMLInputElement;
  declare resultsTarget: HTMLElement;
  declare searchUrlValue: string;
  declare submitUrlValue: string;
  declare communityMemberIdsValue: string[];
  declare headingsValue: string[];

  private debounceTimer?: number;
  private outsideClickHandler = (event: MouseEvent): void => {
    if (!this.element.contains(event.target as Node)) this.close();
  };

  open(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.panelTarget.classList.remove("hidden");
    this.inputTarget.value = "";
    this.inputTarget.focus();
    this.search("");
    document.addEventListener("click", this.outsideClickHandler);
  }

  close(): void {
    this.panelTarget.classList.add("hidden");
    document.removeEventListener("click", this.outsideClickHandler);
  }

  onInput(): void {
    window.clearTimeout(this.debounceTimer);
    const term = this.inputTarget.value;
    this.debounceTimer = window.setTimeout(() => this.search(term), 200);
  }

  async search(term: string): Promise<void> {
    const { data } = await axios.get<TagResult[]>(this.searchUrlValue, {
      params: { search_term: term },
    });
    this.renderResults(data, term);
  }

  renderResults(tags: TagResult[], term: string): void {
    this.resultsTarget.innerHTML = "";

    const trimmedTerm = term.trim();
    const exactMatch = tags.some(
      (tag) => tag.name.toLowerCase() === trimmedTerm.toLowerCase()
    );
    const showAddButtons = trimmedTerm.length > 0 && !exactMatch;

    this.headingsValue.forEach((heading) => {
      const headingTags = tags.filter((tag) => tag.heading === heading);
      if (headingTags.length === 0 && !showAddButtons) return;

      this.resultsTarget.appendChild(this.buildHeadingLabel(heading));
      headingTags.forEach((tag) =>
        this.resultsTarget.appendChild(this.buildRow(tag))
      );

      if (showAddButtons) {
        this.resultsTarget.appendChild(
          this.buildAddButton(heading, trimmedTerm)
        );
      }
    });
  }

  buildHeadingLabel(heading: string): HTMLElement {
    const label = document.createElement("div");
    label.textContent = heading;
    label.className =
      "px-3 pt-2 pb-1 text-xs font-bold uppercase text-neutral-400";
    return label;
  }

  buildAddButton(heading: string, term: string): HTMLElement {
    const addRow = document.createElement("div");
    addRow.className =
      "px-3 py-2 hover:bg-neutral-50 cursor-pointer text-primary-500 font-bold";
    addRow.textContent = `+ Add "${term}" to ${heading}`;
    addRow.addEventListener("click", () => this.assignTag(term, heading));
    return addRow;
  }

  buildRow(tag: TagResult): HTMLElement {
    const row = document.createElement("div");
    row.className =
      "group flex items-center justify-between px-3 py-2 hover:bg-neutral-50 cursor-pointer";

    const label = document.createElement("span");
    label.textContent = tag.name;
    label.className = "flex-1";
    label.addEventListener("click", () => this.assignTag(tag.name));

    const actions = document.createElement("span");
    actions.className = "hidden group-hover:flex items-center gap-2 ml-2";

    const renameBtn = document.createElement("button");
    renameBtn.type = "button";
    renameBtn.textContent = "✏";
    renameBtn.className = "text-neutral-400 hover:text-neutral-700";
    renameBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      this.startRename(row, tag);
    });

    const deactivateBtn = document.createElement("button");
    deactivateBtn.type = "button";
    deactivateBtn.textContent = "⊘";
    deactivateBtn.title = "Deactivate tag";
    deactivateBtn.className = "text-neutral-400 hover:text-error-600";
    deactivateBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      this.deactivateTag(tag);
    });

    actions.append(renameBtn, deactivateBtn);
    row.append(label, actions);

    return row;
  }

  startRename(row: HTMLElement, tag: TagResult): void {
    const currentTerm = this.inputTarget.value;
    row.innerHTML = "";

    const input = document.createElement("input");
    input.type = "text";
    input.value = tag.name;
    input.className = "form-control flex-1";

    let settled = false;
    const save = async () => {
      if (settled) return;
      settled = true;

      const newName = input.value.trim();
      if (newName.length === 0 || newName === tag.name) {
        this.search(currentTerm);
        return;
      }

      await axios.patch(
        `${this.searchUrlValue}/${tag.id}`,
        { tag: { name: newName } },
        { headers: { "X-CSRF-Token": csrfToken() } }
      );
      this.search(currentTerm);
    };

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") save();
      if (event.key === "Escape") {
        settled = true;
        this.search(currentTerm);
      }
    });
    input.addEventListener("blur", save);
    input.addEventListener("click", (event) => event.stopPropagation());

    row.appendChild(input);
    input.focus();
  }

  async deactivateTag(tag: TagResult): Promise<void> {
    const confirmed = window.confirm(
      `Deactivate "${
        tag.name
      }"? It won't be available to add to new Weployees, but the ${
        tag.usage_count
      } Weployee${
        tag.usage_count === 1 ? "" : "s"
      } currently tagged will keep it.`
    );
    if (!confirmed) return;

    const currentTerm = this.inputTarget.value;

    await axios.delete(`${this.searchUrlValue}/${tag.id}`, {
      headers: { "X-CSRF-Token": csrfToken() },
    });
    this.search(currentTerm);
  }

  targetCommunityMemberIds(): string[] {
    if (this.communityMemberIdsValue.length > 0) {
      return this.communityMemberIdsValue;
    }

    return Array.from(
      document.querySelectorAll<HTMLInputElement>(
        'input[name="specific_community_member_ids[]"]:checked'
      )
    ).map((checkbox) => checkbox.value);
  }

  async assignTag(name: string, heading?: string): Promise<void> {
    const communityMemberIds = this.targetCommunityMemberIds();
    if (communityMemberIds.length === 0) return;

    const formData = new URLSearchParams();
    communityMemberIds.forEach((id) =>
      formData.append("specific_community_member_ids[]", id)
    );
    formData.append("tag_community_members[tag_names][]", name);
    if (heading) formData.append("tag_community_members[heading]", heading);

    const { data } = await axios.post(this.submitUrlValue, formData, {
      headers: {
        "X-CSRF-Token": csrfToken(),
        Accept: "application/json",
      },
    });

    this.close();
    applyReplaceContent(data.replace_content);
  }
}

function applyReplaceContent(replaceContent?: Record<string, string>): void {
  if (!replaceContent) return;

  Object.keys(replaceContent).forEach((selectorId) => {
    const element = document.getElementById(selectorId);
    if (element) {
      element.innerHTML = replaceContent[selectorId];
    }
  });
}
